/**
 * Thin fetch wrapper for the Bankom Portal API.
 *
 * Always sends `credentials: 'include'` so the httpOnly auth cookies travel
 * with every request. On 401 we transparently try `/api/auth/refresh` once
 * and re-issue the original request — that hides the 15-minute access-token
 * rotation from the rest of the app.
 *
 * This is intentionally lightweight (no React Query, no axios) because the
 * portal needs few client-side caching tricks. We can layer them in later.
 */

export type ApiError = {
  status: number;
  code: string;
  message: string;
  details?: string[];
};

const API_BASE = "/api";

let refreshInFlight: Promise<boolean> | null = null;

/**
 * Called once when a request 401s and the token refresh also fails — i.e. the
 * session is truly gone (refresh token expired/revoked). AuthContext registers
 * a handler that flips the app to anonymous so route guards redirect to /login
 * instead of widgets polling 401s against a dead session forever.
 */
let sessionExpiredHandler: (() => void) | null = null;
let sessionExpiredNotified = false;
let accessPolicyVersion: number | undefined;
let accessPolicyChangedHandler: (() => void) | null = null;

export function setAccessPolicyChangedHandler(handler: (() => void) | null) {
  accessPolicyChangedHandler = handler;
}

export function setKnownAccessPolicyVersion(version: number | undefined) {
  accessPolicyVersion = version;
}

function observeAccessPolicy(res: Response, path: string) {
  if (path.startsWith("/auth/")) return;
  const raw = res.headers.get("X-Access-Policy-Version");
  const next = raw === null ? undefined : Number(raw);
  if (res.status === 403 || (next !== undefined && Number.isFinite(next)
      && accessPolicyVersion !== undefined && next > accessPolicyVersion)) {
    accessPolicyChangedHandler?.();
  }
}

export function setSessionExpiredHandler(fn: (() => void) | null) {
  sessionExpiredHandler = fn;
  sessionExpiredNotified = false;
}

function notifySessionExpired() {
  if (sessionExpiredNotified) return; // one burst of concurrent 401s → one notification
  sessionExpiredNotified = true;
  sessionExpiredHandler?.();
}

/** Re-arm the expiry notification after a successful login/refresh. */
export function resetSessionExpiredNotice() {
  sessionExpiredNotified = false;
}

async function attemptRefresh(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = fetch(`${API_BASE}/auth/refresh`, {
      method: "POST",
      credentials: "include",
    })
      .then((res) => res.ok)
      .catch(() => false)
      .finally(() => {
        // Reset on next tick so callers piggybacking on the same in-flight refresh
        // all observe the same outcome.
        setTimeout(() => {
          refreshInFlight = null;
        }, 0);
      });
  }
  return refreshInFlight;
}

/**
 * For a request apiFetch does not make itself — the chat's stream: after its 401, refresh the session once as
 * apiFetch would, and report a session that is truly gone the same way.
 */
export async function refreshAfterUnauthorized(): Promise<boolean> {
  const refreshed = await attemptRefresh();
  if (!refreshed) notifySessionExpired();
  return refreshed;
}

/** The error a response carries, in the shape every API call throws. */
export async function readError(res: Response): Promise<ApiError> {
  let body: Partial<ApiError> = {};
  try {
    body = (await res.json()) as Partial<ApiError>;
  } catch {
    /* ignore — non-JSON error */
  }
  return {
    status: res.status,
    code: body.code ?? `HTTP_${res.status}`,
    message: body.message ?? res.statusText,
    details: body.details,
  };
}

export type RequestOptions = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  /** When true, do not attempt token refresh on 401 (use for /auth/login itself). */
  skipRefresh?: boolean;
  signal?: AbortSignal;
};

export async function apiFetch<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const init: RequestInit = {
    method: opts.method ?? "GET",
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(opts.body !== undefined ? { "Content-Type": "application/json" } : {}),
    },
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  };

  let res = await fetch(`${API_BASE}${path}`, init);

  if (res.status === 401 && !opts.skipRefresh) {
    const refreshed = await attemptRefresh();
    if (refreshed) {
      res = await fetch(`${API_BASE}${path}`, init);
    } else {
      notifySessionExpired();
    }
  }

  observeAccessPolicy(res, path);
  if (!res.ok) {
    throw await readError(res);
  }
  if (res.status === 204) {
    return undefined as T;
  }
  return (await res.json()) as T;
}
